import React, { useState, useEffect } from 'react';
import Button from '../base/Button';

interface Slide {
  id: number;
  videoSrc: string;
  title: string;
  subtitle?: string;
  ctaText: string;
  ctaLink: string;
}

const VideoSlider: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides: Slide[] = [
    {
      id: 1,
      videoSrc: 'https://foundersfund.com/wp-content/uploads/2023/06/FF_SPACEX_SLIDE_02.mp4',
      title: 'A Network of Satellites Connecting the World',
      ctaText: 'SpaceX',
      ctaLink: 'https://www.spacex.com'
    },
    {
      id: 2,
      videoSrc: 'https://foundersfund.com/wp-content/uploads/2023/06/FF_PALANTIR_SLIDE_03.mp4',
      title: 'Find the Signal in the Noise',
      ctaText: 'Palantir',
      ctaLink: 'https://www.palantir.com'
    },
    {
      id: 3,
      videoSrc: 'https://public.readdy.ai/ai/img_res/136369b55fdc9f8ed6a7dc3dfa9965ec.jpg',
      title: 'The Future of Electric Mobility',
      ctaText: 'Tesla',
      ctaLink: 'https://www.tesla.com'
    },
    {
      id: 4,
      videoSrc: 'https://readdy.ai/api/search-image?query=Advanced%20AI%20data%20center%20with%20powerful%20GPU%20servers%20and%20neural%20network%20visualization%20in%20dramatic%20black%20and%20white%20monochrome%20photography%2C%20showcasing%20artificial%20intelligence%20and%20machine%20learning%20infrastructure%2C%20high-tech%20industrial%20style%20with%20elegant%20shadows%20and%20modern%20architecture%2C%20grayscale%20filter%20applied&width=1920&height=1080&seq=nvidia-bw-video-1&orientation=landscape',
      title: 'Accelerating AI Innovation',
      ctaText: 'Nvidia',
      ctaLink: 'https://www.nvidia.com'
    },
    {
      id: 5,
      videoSrc: 'https://readdy.ai/api/search-image?query=Cutting-edge%20semiconductor%20manufacturing%20facility%20with%20advanced%20chip%20production%20equipment%20in%20dramatic%20black%20and%20white%20monochrome%20photography%2C%20representing%20next-generation%20computing%20technology%20and%20innovation%2C%20industrial%20photography%20style%20with%20high%20contrast%20lighting%20and%20modern%20clean%20room%20environment%2C%20grayscale%20filter%20applied&width=1920&height=1080&seq=nvidia-chip-bw-video-1&orientation=landscape',
      title: 'Powering the Future of Computing',
      ctaText: 'Nvidia',
      ctaLink: 'https://www.nvidia.com'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [slides.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <div className="relative h-screen overflow-hidden">
      {/* Background overlay */}
      <div className="absolute inset-0 bg-black/30 z-10"></div>
      
      {/* Video/Image slides */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100 z-20' : 'opacity-0 z-10'
          }`}
        >
          {slide.videoSrc.includes('foundersfund.com') ? (
            <video
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
            >
              <source src={slide.videoSrc} type="video/mp4" />
            </video>
          ) : (
            <img
              src={slide.videoSrc}
              alt={slide.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
        </div>
      ))}

      {/* Content overlay */}
      <div className="absolute inset-0 z-30 flex items-end">
        <div className="w-full px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20">
          <div className="max-w-4xl">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {slides[currentSlide].title}
            </h2>
            <Button
              variant="ghost"
              size="lg"
              href={slides[currentSlide].ctaLink}
              target={slides[currentSlide].ctaLink.startsWith('http') ? '_blank' : undefined}
              className="group"
            >
              <span className="mr-2">{slides[currentSlide].ctaText}</span>
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform duration-200"></i>
            </Button>
          </div>
        </div>
      </div>

      {/* Pagination dots */}
      <div className="absolute bottom-6 right-6 z-40 flex space-x-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentSlide
                ? 'bg-white scale-125'
                : 'bg-white/50 hover:bg-white/75'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default VideoSlider;
